// axiosCurlLogger.ts
import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

interface EndpointDescription {
  path: string;
  method: string;
  description: string;
}

const styleConstants = {
  heading: [
    'background: #2563eb',
    'color: white',
    'padding: 4px 8px',
    'border-radius: 4px',
    'font-weight: bold',
  ].join(';'),

  errorHeading: [
    'background: #dc2626',
    'color: white',
    'padding: 4px 8px',
    'border-radius: 4px',
    'font-weight: bold',
  ].join(';'),

  borderTop: ['color: #64748b', 'font-weight: bold'].join(';'),

  borderBottom: ['color: #64748b', 'font-weight: bold'].join(';'),

  apiDescription: [
    'color: #0ea5e9',
    'font-size: 14px',
    'font-weight: bold',
    'font-family: monospace',
  ].join(';'),

  apiPath: [
    'background: #1e293b',
    'color: #38bdf8',
    'padding: 2px 6px',
    'border-radius: 3px',
    'font-family: monospace',
  ].join(';'),

  curl: ['color: #6366f1', 'font-weight: bold'].join(';'),

  method: ['color: #2563eb', 'font-weight: bold'].join(';'),

  url: ['color: #059669', 'font-weight: bold'].join(';'),

  params: ['color: #7c3aed'].join(';'),

  headerFlag: ['color: #ea580c', 'font-weight: bold'].join(';'),

  headerName: ['color: #f0bb78'].join(';'),

  headerValue: ['color: #2563eb'].join(';'),

  dataFlag: ['color: #db2777', 'font-weight: bold'].join(';'),

  dataContent: ['color: #9333ea'].join(';'),
};

const borders = {
  top: '='.repeat(40),
  bottom: '='.repeat(40),
};

const pathMatches = (templatePath: string, actualPath: string): boolean => {
  const templateParts = templatePath.split('/');
  const actualParts = actualPath.split('/');

  if (templateParts.length !== actualParts.length) {
    return false;
  }

  return templateParts.every((templatePart, index) => {
    // If template part is a parameter (enclosed in {}), it matches anything
    if (templatePart.startsWith('{') && templatePart.endsWith('}')) {
      return true;
    }
    return templatePart === actualParts[index];
  });
};

const getApiDescription = (config: AxiosRequestConfig): string => {
  const method = config.method?.toUpperCase() || 'GET';
  const url = config.url || '';

  // Extract the endpoint path - remove query params if present
  const endpoint = url.split('?')[0];

  // Clean up the endpoint - remove leading/trailing slashes
  const cleanEndpoint = endpoint.replace(/^\/+|\/+$/g, '');

  // Try to find a matching description
  const matchingDescription = endpointDescriptions.find(
    desc => desc.method === method && pathMatches(desc.path, cleanEndpoint),
  );

  if (matchingDescription) {
    return `${method} → ${matchingDescription.description}`;
  }
  // Fallback to the original behavior if no match is found
  const pathSegments = cleanEndpoint.split('/');
  const fallbackDescription = pathSegments
    .map(segment => {
      return segment
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    })
    .join(' → ');

  return `${method} → ${fallbackDescription}`;
};

// ----------------------------------------------------------------------
// SHARED CURL BUILDING HELPERS (used by both console-styled `generateCurl`
// and plain-text `buildPlainCurl`). Any change here applies to both
// outputs automatically.
// ----------------------------------------------------------------------

type CurlConfigSnapshot = {
  method?: string;
  url?: string;
  baseURL?: string;
  headers?: Record<string, unknown>;
  params?: Record<string, unknown>;
  data?: unknown;
};

const HEADER_SKIP = new Set([
  'common',
  'delete',
  'get',
  'post',
  'put',
  'patch',
  'user-agent',
  'skip-curl-log',
]);

// Resolve the customer-visible API key for inserting into the Authorization
// header in the styled-console output. Falls back to a placeholder so
// pasting the printed curl still self-documents what to fill in.
const resolveApiKey = (): string => {
  try {
    const permRaw = localStorage.getItem('rolePermission');
    const perm = permRaw ? JSON.parse(permRaw) : {};
    const localApi = localStorage.getItem('ApiKey');
    if (localApi && perm?.viewApiKey) return localApi;
  } catch {
    /* fall through */
  }
  return 'YOUR_APIKEY_FROM_SETTINGS';
};

// Build the full URL: baseURL + path + ?params. Returns the URL alone plus
// the optional query-string fragment separately so the styled emitter can
// colour the query part differently.
const buildUrl = (
  config: CurlConfigSnapshot,
): { url: string; query: string } => {
  const urlPath = config.url?.startsWith('/') ? config.url : `/${config.url}`;
  const base = config.baseURL ? `${config.baseURL}${urlPath}` : urlPath;
  const query = config.params
    ? new URLSearchParams(config.params as Record<string, string>).toString()
    : '';
  return { url: base, query };
};

// Walk request headers honouring the skip list. Optionally inject
// Content-Type when the request has a body. Yields canonical [name, value]
// tuples — emitter decides how to format each.
const collectHeaders = (
  config: CurlConfigSnapshot,
  injectContentType: boolean,
): Array<[string, string]> => {
  const headers = { ...(config.headers || {}) } as Record<string, unknown>;
  if (
    injectContentType &&
    config.data !== undefined &&
    !headers['Content-Type']
  ) {
    headers['Content-Type'] = 'application/json';
  }
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(headers)) {
    if (HEADER_SKIP.has(k.toLowerCase()) || v === undefined) continue;
    out.push([k, String(v)]);
  }
  return out;
};

// Sanitize the request body for emission. Drops fields that are internal-
// only Heltar metadata so the curl reflects the real public API contract.
// Today: messages/send `refId` (server-side bookkeeping) and
// `templateContent` (frontend-only precomputed preview body). Add other
// per-endpoint strips here as new internal fields surface.
const sanitizeBody = (config: CurlConfigSnapshot): unknown => {
  const data = config.data;
  if (data === undefined || data === null) return data;
  if (typeof data === 'string') return data;

  if (
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray((data as { messages?: unknown[] }).messages) &&
    config.url?.includes('messages/send')
  ) {
    const cloned = JSON.parse(JSON.stringify(data)) as {
      messages: Record<string, unknown>[];
    };
    cloned.messages = cloned.messages.map(msg => {
      const { refId: _r, templateContent: _t, ...rest } = msg;
      return rest;
    });
    return cloned;
  }
  return data;
};

// ----------------------------------------------------------------------
// STYLED CONSOLE EMITTER — preserves the pre-refactor browser-console UX.
// Reuses the shared helpers above.
// ----------------------------------------------------------------------

const formatHeaderStyled = (
  key: string,
  value: string,
): { text: string; styles: string[] } => {
  const updatedValue =
    key === 'Authorization' ? `Bearer ${resolveApiKey()}` : value;
  return {
    text: `%c-H%c '${key}%c: ${updatedValue}%c'`,
    styles: [
      styleConstants.headerFlag,
      styleConstants.headerName,
      styleConstants.headerValue,
      '',
    ],
  };
};

const generateCurl = (
  config: AxiosRequestConfig,
): { text: string; styles: string[] } => {
  const method = config.method?.toUpperCase() || 'GET';
  const { url, query } = buildUrl(config as CurlConfigSnapshot);

  const parts: string[] = [];
  const styleList: string[] = [];

  parts.push('%ccurl%c');
  styleList.push(styleConstants.curl, '');

  parts.push(`%c-X%c %c${method}%c`);
  styleList.push(styleConstants.headerFlag, '', styleConstants.method, '');

  if (query) {
    parts.push(`'${url}%c?${query}%c'`);
    styleList.push(styleConstants.url, styleConstants.params, '');
  } else {
    parts.push(`%c'${url}'%c`);
    styleList.push(styleConstants.url, '');
  }

  for (const [k, v] of collectHeaders(config as CurlConfigSnapshot, true)) {
    const { text, styles } = formatHeaderStyled(k, v);
    parts.push(text);
    styleList.push(...styles);
  }

  if (config.data !== undefined) {
    const sanitized = sanitizeBody(config as CurlConfigSnapshot);
    const dataStr =
      typeof sanitized === 'string' ? sanitized : JSON.stringify(sanitized);
    parts.push(`%c-d%c %c'${dataStr}'%c`);
    styleList.push(styleConstants.dataFlag, '', styleConstants.dataContent, '');
  }

  return { text: parts.join(' '), styles: styleList };
};

export const isDeveloperMode = (): boolean => {
  try {
    return localStorage.getItem('DeveloperMode') === 'true';
  } catch {
    return false;
  }
};

export const setDeveloperMode = (on: boolean) => {
  try {
    localStorage.setItem('DeveloperMode', on ? 'true' : 'false');
    curlLogStore.notifyMode();
  } catch {
    /* ignore */
  }
};

// ----------------------------------------------------------------------
// PLAIN-TEXT EMITTER — for the floating dev panel + clipboard. Reuses the
// shared helpers; only Authorization handling differs (redacted by default
// because panel content may be screenshotted).
// ----------------------------------------------------------------------

const buildPlainCurl = (
  config: CurlConfigSnapshot,
  showSecrets: boolean,
): string => {
  const method = config.method?.toUpperCase() || 'GET';
  const { url, query } = buildUrl(config);
  const fullUrl = query ? `${url}?${query}` : url;

  const lines: string[] = [`curl -X ${method} '${fullUrl}'`];

  for (const [k, v] of collectHeaders(config, true)) {
    const value =
      k.toLowerCase() === 'authorization' && !showSecrets ? '<REDACTED>' : v;
    lines.push(`  -H '${k}: ${value}'`);
  }

  if (config.data !== undefined) {
    const sanitized = sanitizeBody(config);
    const dataStr =
      typeof sanitized === 'string' ? sanitized : JSON.stringify(sanitized);
    lines.push(`  -d '${dataStr}'`);
  }

  return lines.join(' \\\n');
};

// ----------------------------------------------------------------------
// Subscribable in-memory log store. Populated by curlLogger; consumed by
// the floating <CurlDevPanel /> component. Keeps the last 200 entries.
// ----------------------------------------------------------------------

export type CurlLogEntry = {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  description: string;
  status?: number;
  ok?: boolean;
  durationMs?: number;
  errorMessage?: string;
  // Snapshot of the config so the panel can rebuild the curl on demand
  // (e.g. when user toggles "show secrets").
  config: {
    method?: string;
    url?: string;
    baseURL?: string;
    headers?: Record<string, unknown>;
    params?: Record<string, unknown>;
    data?: unknown;
  };
};

const MAX_ENTRIES = 200;

type Listener = () => void;

const curlLogStore = {
  entries: [] as CurlLogEntry[],
  listeners: new Set<Listener>(),
  modeListeners: new Set<Listener>(),

  append(entry: CurlLogEntry) {
    this.entries = [...this.entries, entry].slice(-MAX_ENTRIES);
    this.listeners.forEach(fn => fn());
  },
  update(id: string, patch: Partial<CurlLogEntry>) {
    let changed = false;
    this.entries = this.entries.map(e => {
      if (e.id !== id) return e;
      changed = true;
      return { ...e, ...patch };
    });
    if (changed) this.listeners.forEach(fn => fn());
  },
  clear() {
    this.entries = [];
    this.listeners.forEach(fn => fn());
  },
  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  },
  subscribeMode(fn: Listener) {
    this.modeListeners.add(fn);
    return () => {
      this.modeListeners.delete(fn);
    };
  },
  notifyMode() {
    this.modeListeners.forEach(fn => fn());
  },
};

export const curlLog = {
  getEntries: () => curlLogStore.entries,
  subscribe: curlLogStore.subscribe.bind(curlLogStore),
  subscribeMode: curlLogStore.subscribeMode.bind(curlLogStore),
  clear: curlLogStore.clear.bind(curlLogStore),
  buildPlainCurl,
};

const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const cfgSnapshot = (config: AxiosRequestConfig): CurlLogEntry['config'] => ({
  method: config.method,
  url: config.url,
  baseURL: config.baseURL,
  headers: config.headers as Record<string, unknown> | undefined,
  params: config.params as Record<string, unknown> | undefined,
  data: config.data,
});

// Curl-log id attached to the config so request/response/error can be paired.
type ConfigWithCurlId = AxiosRequestConfig & { __curlLogId?: string };

// Emit the styled curl block to the browser console. Single owner for the
// 5-line console.log pattern — request + error paths both call it. Heading
// style chosen by `kind` so success and failure render the obvious colours.
const logToConsole = (
  config: AxiosRequestConfig,
  apiDesc: string,
  kind: 'request' | 'error',
) => {
  const { text, styles } = generateCurl(config);
  const heading =
    kind === 'error'
      ? {
          label: 'cURL For Failed API Call',
          style: styleConstants.errorHeading,
        }
      : { label: 'cURL For API Call', style: styleConstants.heading };
  console.log('\n%c' + borders.top, styleConstants.borderTop);
  console.log(
    '%c📡 API Details: %c' + apiDesc,
    styleConstants.apiDescription,
    styleConstants.apiPath,
  );
  console.log(`%c${heading.label}`, heading.style);
  console.log(text, ...styles);
  console.log('%c' + borders.bottom + '\n', styleConstants.borderBottom);
};

// Build a fresh store entry from an axios config. Shared by the request
// interceptor (normal path) and the error interceptor's "pre-interceptor
// error" fallback (rare — request side never ran).
const makeStoreEntry = (
  config: AxiosRequestConfig,
  id: string,
  apiDesc: string,
  patch: Partial<CurlLogEntry> = {},
): CurlLogEntry => ({
  id,
  timestamp: Date.now(),
  method: (config.method || 'GET').toUpperCase(),
  url: config.url || '',
  description: apiDesc,
  config: cfgSnapshot(config),
  ...patch,
});

// In-memory store ALWAYS receives entries (so flipping DeveloperMode on
// shows recent history, not just future calls). Only the styled-console
// output is gated by DeveloperMode — otherwise the console fills up for
// regular users. Panel visibility is gated separately inside <CurlDevPanel/>.
export const curlLogger = {
  request: <T extends InternalAxiosRequestConfig>(config: T): T => {
    if (config.headers?.['skip-curl-log']) return config;

    const id = newId();
    (config as ConfigWithCurlId).__curlLogId = id;
    const apiDesc = getApiDescription(config);

    if (isDeveloperMode()) logToConsole(config, apiDesc, 'request');
    curlLogStore.append(makeStoreEntry(config, id, apiDesc));
    return config;
  },

  response: (response: AxiosResponse) => {
    const id = (response.config as ConfigWithCurlId).__curlLogId;
    if (!id) return response;
    const existing = curlLogStore.entries.find(e => e.id === id);
    curlLogStore.update(id, {
      status: response.status,
      ok: true,
      durationMs: existing ? Date.now() - existing.timestamp : undefined,
    });
    return response;
  },

  error: (error: AxiosError) => {
    if (!error.config) return Promise.reject(error);
    if (error.config.headers?.['skip-curl-log']) return Promise.reject(error);

    const apiDesc = getApiDescription(error.config);
    if (isDeveloperMode()) logToConsole(error.config, apiDesc, 'error');

    const id = (error.config as ConfigWithCurlId).__curlLogId;
    const errorPatch = {
      status: error.response?.status,
      ok: false,
      errorMessage: error.message,
    };
    if (id) {
      const existing = curlLogStore.entries.find(e => e.id === id);
      curlLogStore.update(id, {
        ...errorPatch,
        durationMs: existing ? Date.now() - existing.timestamp : undefined,
      });
    } else {
      // Rare: pre-interceptor error means request side never logged. Append
      // a fresh entry so the panel still shows the failure.
      curlLogStore.append(
        makeStoreEntry(error.config, newId(), apiDesc, errorPatch),
      );
    }
    return Promise.reject(error);
  },
};

const endpointDescriptions: EndpointDescription[] = [
  //get
  {
    path: 'flows',
    method: 'GET',
    description: 'Get all flows from this curl',
  },
  {
    path: 'templates',
    method: 'GET',
    description: 'Get all templates from this curl',
  },
  {
    path: 'role-permission',
    method: 'GET',
    description: 'Get role permissions data from this curl',
  },
  {
    path: 'business',
    method: 'GET',
    description: 'Get business details',
  },
  {
    path: 'templates/media-link',
    method: 'GET',
    description: 'Get template media link',
  },
  {
    path: 'clients',
    method: 'GET',
    description: 'Get clients from this curl',
  },
  {
    path: 'app-store/apps',
    method: 'GET',
    description: 'Get apps details',
  },
  {
    path: 'auth/business-employees',
    method: 'GET',
    description: 'Get employee details',
  },
  {
    path: 'business/profile',
    method: 'GET',
    description: 'Get profile information',
  },
  {
    path: 'business/commerce',
    method: 'GET',
    description: 'Get commerce details',
  },
  {
    path: 'campaigns',
    method: 'GET',
    description: 'Get campaign details',
  },
  //post
  {
    path: 'clients',
    method: 'POST',
    description: 'Update client details or create new client',
  },
  {
    path: 'messages/send',
    method: 'POST',
    description:
      'Send messages curl. If you send a template, then passing template content is optional. You can remove the template content. Template content is the body of the template. ',
  },
  {
    path: 'clients/chat/assign',
    method: 'POST',
    description: 'Assign or unasssign a chat to employee',
  },
  {
    path: 'business/attributes',
    method: 'POST',
    description: 'Create a attribute',
  },
  {
    path: 'campaigns/send',
    method: 'POST',
    description: 'Send a campaign',
  },
  //put
  {
    path: 'clients/chat/toggle/{phoneNumber}',
    method: 'PUT',
    description: 'Toggle chat status (closed/open) for a specific client',
  },
  {
    path: 'business/attributes',
    method: 'PUT',
    description: 'Update a attribute',
  },
  //delete
  {
    path: 'business/attributes',
    method: 'DELETE',
    description: 'Delete a attribute',
  },
];
